/**
 * @copyright Copyright (c) 2024-2026 Ronan LE MEILLAT
 * @license AGPL-3.0-or-later
 */

import type {
  Auth0ManagementTokenApiResponse,
  Auth0User,
  Auth0Permission,
} from "../types/auth0.types";

import { FC, ReactNode, useEffect, useState } from "react";
import { mutate } from "swr";
import { Button } from "@heroui/button";
import { Tooltip } from "@heroui/tooltip";
import { Link } from "@heroui/link";
import { useTranslation } from "react-i18next";

import { SiteLoading } from "../components/site-loading";

import { useAuth, withAuthentication } from "./providers/use-auth";
import { useUser } from "./providers/user-provider";
export { AccountModal } from "./account-modal";

// Cache for Auth0 Management API calls (5 minutes TTL)
const AUTH0_CACHE_TTL = 300000;
const mgmtCache: Record<string, { data: unknown; timestamp: number }> = {};

const getFromCache = (key: string) => {
  const cached = mgmtCache[key];

  if (cached && Date.now() - cached.timestamp < AUTH0_CACHE_TTL) {
    return cached.data;
  }

  return null;
};

const setToCache = (key: string, data: unknown) => {
  mgmtCache[key] = { data, timestamp: Date.now() };
};

const deleteFromCache = (key: string) => {
  delete mgmtCache[key];
};

/**
 * Clears the cached user list so that the next loadUsers() call
 * fetches fresh data from Auth0 Management API.
 * Call this after saving permissions or blocking/unblocking users.
 */
export const clearUserListCache = () => {
  deleteFromCache("users_list");
};

export const updateUserInCache = (
  userId: string,
  updateFn: (user: Auth0User) => Auth0User,
) => {
  const cached = getFromCache("users_list");

  if (cached && Array.isArray(cached)) {
    const updated = cached.map((u) => (u.user_id === userId ? updateFn(u) : u));

    setToCache("users_list", updated);
  }
};

/**
 * Renders the user's profile name with a tooltip showing their username.
 * @returns The user's name with a tooltip showing their username
 */
export function Profile() {
  const { user: authUser } = useAuth();
  const { user: dbUser } = useUser();

  const name =
    dbUser?.firstname && dbUser?.lastname
      ? `${dbUser.firstname} ${dbUser.lastname}`
      : dbUser?.firstname || dbUser?.lastname || authUser?.name;

  return (
    <Tooltip content={authUser?.nickname} delay={750}>
      <span>{name}</span>
    </Tooltip>
  );
}

/**
 * Renders a login button for authentication.
 * Only shows when the user is not authenticated.
 * @param props - Component props
 * @param [props.text] - Custom text for the button. Defaults to localized "log-in" text.
 * @returns Login button or null if user is already authenticated
 */
export const LoginButton: FC<{ text?: string }> = ({ text }) => {
  const { isAuthenticated, login } = useAuth();
  const { t } = useTranslation();

  if (!text) {
    text = t("log-in");
  }

  return (
    !isAuthenticated && (
      <Button
        className="text-sm font-normal text-default-600 bg-default-100"
        radius="full"
        type="button"
        onPress={() => login()}
      >
        {text}
      </Button>
    )
  );
};

/**
 * Renders a login link for authentication.
 * Only shows when the user is not authenticated.
 * @param props - Component props
 * @param [props.text] - Custom text for the link. Defaults to localized "log-in" text.
 * @returns Login link or null if user is already authenticated
 */
export const LoginLink: FC<{
  text?: string;
  color?:
    | "primary"
    | "foreground"
    | "secondary"
    | "success"
    | "warning"
    | "danger";
  isMobileView?: boolean;
}> = ({ text, color, isMobileView }) => {
  const { isAuthenticated, login } = useAuth();
  const { t } = useTranslation();

  if (!text) {
    text = t("log-in");
  }

  return (
    !isAuthenticated && (
      <Link
        className={
          isMobileView
            ? "w-full flex items-center justify-between px-5 py-4 rounded-2xl border font-black text-lg transition-all active:scale-95 border-red-500/30 bg-red-500/10 text-red-500 hover:text-red-400"
            : ""
        }
        color={isMobileView ? undefined : color}
        size="lg"
        onPress={() => {
          login();
        }}
      >
        <div className="flex items-center justify-between w-full">
          {text}
          {isMobileView && (
            <svg
              className="w-5 h-5 opacity-40 ml-2"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="m8.25 4.5 7.5 7.5-7.5 7.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>
      </Link>
    )
  );
};

interface LogoutButtonProps {
  /**
   * Show the button even if the user is not authenticated
   */
  showButtonIfNotAuthenticated?: boolean;
  /**
   * Custom text for the button
   */
  text?: string;
}

/**
 * Renders a logout button for authentication.
 * By default, only shows when the user is authenticated.
 * @param props - Component props
 * @param [props.showButtonIfNotAuthenticated=false] - When true, shows the button even if user is not authenticated
 * @param [props.text] - Custom text for the button. Defaults to localized "log-out-someone" text filled with user name or ID
 * @returns Logout button or null based on authentication status and showButtonIfNotAuthenticated setting
 */
export const LogoutButton: FC<LogoutButtonProps> = ({
  showButtonIfNotAuthenticated = false,
  text,
}) => {
  const { isAuthenticated, logout, user: authUser } = useAuth();
  const { user: dbUser } = useUser();
  const { t } = useTranslation();

  if (!text) {
    text = t("auth.logout");
  }

  const name =
    dbUser?.firstname && dbUser?.lastname
      ? `${dbUser.firstname} ${dbUser.lastname}`
      : dbUser?.firstname || dbUser?.lastname || authUser?.name || "";

  return (
    (isAuthenticated || showButtonIfNotAuthenticated) && (
      <Tooltip
        content={t("auth.user-details", {
          name: name,
          nickname: authUser?.nickname || "",
          email: authUser?.email || "",
          sub: authUser?.sub || "",
        })}
        delay={750}
      >
        <Button
          className="text-sm font-bold text-white bg-red-600 hover:bg-red-700 shadow-md transform hover:scale-105 transition-all"
          color="danger"
          radius="full"
          type="button"
          onPress={() => {
            logout({
              logoutParams: {
                returnTo: window.location.origin,
              },
            });
          }}
        >
          <span>{text}</span>
        </Button>
      </Tooltip>
    )
  );
};

interface LogoutLinkProps extends LogoutButtonProps {
  /**
   * Button color
   */
  color?:
    | "primary"
    | "foreground"
    | "secondary"
    | "success"
    | "warning"
    | "danger";
  isMobileView?: boolean;
}

/**
 * Renders a logout link for authentication.
 * By default, only shows when the user is authenticated.
 * @param props - Component props
 * @param [props.text] - Custom text for the link. Defaults to localized "log-out-someone" text filled with user name or ID
 * @returns Logout link or null based on authentication status
 */
export const LogoutLink: FC<LogoutLinkProps> = ({
  showButtonIfNotAuthenticated = false,
  text,
  color,
  isMobileView,
}) => {
  const { isAuthenticated, logout, user: authUser } = useAuth();
  const { user: dbUser } = useUser();
  const { t } = useTranslation();

  const name =
    dbUser?.firstname && dbUser?.lastname
      ? `${dbUser.firstname} ${dbUser.lastname}`
      : dbUser?.firstname || dbUser?.lastname || authUser?.name || "";

  if (!text) {
    text = t("log-out-someone", {
      name: name,
    });
  }

  return isAuthenticated || showButtonIfNotAuthenticated ? (
    <Link
      className={
        isMobileView
          ? "w-full flex items-center justify-between px-5 py-4 rounded-2xl border font-black text-lg transition-all active:scale-95 border-pink-500/30 bg-pink-500/10 text-pink-400 mb-2"
          : ""
      }
      color={isMobileView ? undefined : color}
      size="lg"
      onPress={() => {
        logout({
          logoutParams: {
            returnTo: window.location.origin,
          },
        });
      }}
    >
      <div className="flex items-center justify-between w-full">
        {text}
        {isMobileView && (
          <svg
            className="w-5 h-5 opacity-40 ml-2"
            fill="none"
            stroke="currentColor"
            strokeWidth={2.5}
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="m8.25 4.5 7.5 7.5-7.5 7.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        )}
      </div>
    </Link>
  ) : null;
};

/**
 * Conditionally renders either a login or logout button based on authentication status.
 * Provides a convenient way to toggle between auth actions in a single component.
 * @returns Either the LoginButton or LogoutButton component
 */
export const LoginLogoutButton: FC<LogoutButtonProps> = ({
  showButtonIfNotAuthenticated = false,
}) => {
  const { isAuthenticated } = useAuth();
  const { isLoading, setIsAccountModalOpen } = useUser();
  const { t } = useTranslation();

  if (isLoading) return null;

  return isAuthenticated ? (
    <div className="flex items-center gap-2">
      <Button
        className="font-semibold text-default-700 bg-default-100/50 hover:bg-default-200/50"
        color="default"
        radius="full"
        variant="flat"
        onPress={() => setIsAccountModalOpen(true)}
      >
        {t("auth.account")}
      </Button>
      <LogoutButton
        showButtonIfNotAuthenticated={showButtonIfNotAuthenticated}
        text={t("auth.logout")}
      />
    </div>
  ) : (
    <LoginButton />
  );
};

/**
 * Conditionally renders either a login or logout link based on authentication status.
 * Provides a convenient way to toggle between auth actions in a single component.
 * @returns Either the LoginLink or LogoutLink component
 */
export const LoginLogoutLink: FC<LogoutLinkProps> = ({
  showButtonIfNotAuthenticated = false,
  text,
  color,
  isMobileView,
}) => {
  const { isAuthenticated } = useAuth();
  const { isLoading, setIsAccountModalOpen } = useUser();
  const { t } = useTranslation();

  if (isLoading) return null;

  return isAuthenticated ? (
    <div className="flex flex-col">
      <Link
        className={
          isMobileView
            ? "w-full flex items-center justify-between px-5 py-4 rounded-2xl border font-black text-lg transition-all active:scale-95 border-white/20 bg-white/5 text-white mb-2"
            : "font-bold cursor-pointer"
        }
        color={isMobileView ? undefined : "foreground"}
        size="lg"
        onPress={() => setIsAccountModalOpen(true)}
      >
        <div className="flex items-center justify-between w-full">
          {t("auth.account")}
          {isMobileView && (
            <svg
              className="w-5 h-5 opacity-40 ml-2"
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="m8.25 4.5 7.5 7.5-7.5 7.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          )}
        </div>
      </Link>
      <LogoutLink
        color={color}
        isMobileView={isMobileView}
        showButtonIfNotAuthenticated={showButtonIfNotAuthenticated}
        text={text}
      />
    </div>
  ) : (
    <LoginLink isMobileView={isMobileView} />
  );
};

/**
 * Higher-order component that protects routes requiring authentication.
 * Redirects unauthenticated users to the login page and shows a loading state during the redirect.
 * @param props - Component props
 * @param props.component - The component to render when the user is authenticated
 * @returns The protected component or loading state
 * @example
 * ```tsx
 * <AuthenticationGuard component={ProtectedDashboard} />
 * ```
 */
export const AuthenticationGuard: FC<{ component: FC }> = ({ component }) => {
  const Component = withAuthentication(component, {
    onRedirecting: () => <SiteLoading />,
  });

  return <Component />;
};

/**
 * Component that conditionally renders its children based on whether the user has a specific permission.
 *
 * @param props - Component props
 * @param props.permission - The required permission to display the content
 * @param props.children - The child elements to display if the user has the permission
 * @param props.fallback - Optional element to display if the user lacks the permission (defaults to empty fragment)
 * @returns The children if the user has the permission, otherwise the fallback
 *
 * @example
 * ```tsx
 * <AuthenticationGuardWithPermission permission="read:api">
 *   <ProtectedComponent />
 * </AuthenticationGuardWithPermission>
 * ```
 */
export const AuthenticationGuardWithPermission: FC<{
  permission: string;
  children: ReactNode;
  fallback?: ReactNode;
}> = ({ permission, children, fallback = <></> }) => {
  const { hasPermission } = useAuth();
  const [permitted, setPermitted] = useState<boolean | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const checkPermission = async () => {
      try {
        const result = await hasPermission(permission);

        if (isMounted) {
          setPermitted(result);
          setIsLoading(false);
        }
      } catch (error) {
        console.error("Permission check failed:", error);
        if (isMounted) {
          setPermitted(false);
          setIsLoading(false);
        }
      }
    };

    checkPermission();

    // Cleanup function to prevent state updates on unmounted component
    return () => {
      isMounted = false;
    };
  }, [permission, hasPermission]);

  if (isLoading) {
    return <SiteLoading />;
  }

  return permitted ? <>{children}</> : <>{fallback}</>;
};

/**
 * Custom hook that provides secured API fetching capabilities
 * @returns Object with methods for secured API operations
 * @example
 * ```tsx
 * const { getJson, postJson, deleteJson, hasPermission, putJson } = useSecuredApi();
 * const data = await getJson('https://api.example.com/data');
 * await postJson('https://api.example.com/data', { key: 'value' });
 * await deleteJson('https://api.example.com/data/1');
 * await putJson('https://api.example.com/data/1', { key: 'newValue' });
 * ```
 */
export const useSecuredApi = () => {
  const {
    getJson,
    postJson,
    deleteJson,
    hasPermission,
    putJson,
    getAccessToken,
  } = useAuth();

  /**
   * Obtient un token Auth0 Management API via le worker (avec cache KV).
   * Nécessite la permission `auth0:admin:api`.
   */
  const getAuth0ManagementToken =
    async (): Promise<Auth0ManagementTokenApiResponse> => {
      const apiBase = import.meta.env.API_BASE_URL || "";
      const result = await postJson<Auth0ManagementTokenApiResponse>(
        `${apiBase}/api/__auth0/token`,
        {},
      );

      return result;
    };

  const auth0Domain =
    import.meta.env.VITE_AUTH0_DOMAIN || import.meta.env.AUTH0_DOMAIN || "";

  /**
   * Liste tous les utilisateurs depuis Auth0 Management API.
   * @param mgmtToken Token Auth0 Management API obtenu via getAuth0ManagementToken()
   */
  const listAuth0Users = async (mgmtToken: string): Promise<Auth0User[]> => {
    const cacheKey = "users_list";
    const cached = getFromCache(cacheKey) as Auth0User[] | null;

    if (cached) return cached;

    const resp = await fetch(
      `https://${auth0Domain}/api/v2/users?per_page=100&include_totals=false`,
      {
        headers: {
          Authorization: `Bearer ${mgmtToken}`,
          "Content-Type": "application/json",
        },
      },
    );

    if (!resp.ok) {
      const errorText = await resp.text().catch(() => "");

      throw new Error(errorText || `Auth0 API error: ${resp.status}`);
    }

    const contentType = resp.headers.get("Content-Type");

    if (!contentType || !contentType.includes("application/json")) {
      throw new Error("Invalid response format from Auth0: Expected JSON");
    }

    const data = (await resp.json()) as Auth0User[];

    setToCache(cacheKey, data);

    return data;
  };

  /**
   * Récupère les permissions d'un utilisateur Auth0.
   * @param mgmtToken Token Auth0 Management API
   * @param userId Identifiant Auth0 de l'utilisateur (ex: auth0|xxx)
   */
  const getUserPermissions = async (
    mgmtToken: string,
    userId: string,
  ): Promise<Auth0Permission[]> => {
    const encodedId = encodeURIComponent(userId);
    const resp = await fetch(
      `https://${auth0Domain}/api/v2/users/${encodedId}/permissions`,
      {
        headers: {
          Authorization: `Bearer ${mgmtToken}`,
          "Content-Type": "application/json",
        },
      },
    );

    if (!resp.ok) throw new Error(await resp.text());

    return (await resp.json()) as Auth0Permission[];
  };

  /**
   * Add multiple permissions to an Auth0 user
   * @param mgmtToken Token Auth0 Management API
   * @param userId Auth0 user ID (ex: auth0|xxx)
   * @param permissionNames Array of permission names (ex: ['exercises:read', 'admin:users'])
   */
  const addPermissionsToUser = async (
    mgmtToken: string,
    userId: string,
    permissionNames: string[],
  ): Promise<void> => {
    if (permissionNames.length === 0) return;
    const apiBase = import.meta.env.API_BASE_URL || "";
    const audience = import.meta.env.AUTH0_AUDIENCE || apiBase;
    const encodedId = encodeURIComponent(userId);

    const permissionsPayload = permissionNames.map((name) => ({
      resource_server_identifier: audience,
      permission_name: name,
    }));

    const resp = await fetch(
      `https://${auth0Domain}/api/v2/users/${encodedId}/permissions`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${mgmtToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          permissions: permissionsPayload,
        }),
      },
    );

    if (!resp.ok) throw new Error(await resp.text());
  };

  /**
   * Add a permission to a Auth0 user
   * @param mgmtToken Token Auth0 Management API
   * @param userId Auth0 user ID (ex: auth0|xxx)
   * @param permissionName Permission name (ex: exercises:read)
   */
  const addPermissionToUser = async (
    mgmtToken: string,
    userId: string,
    permissionName: string,
  ): Promise<void> => {
    const apiBase = import.meta.env.API_BASE_URL || "";
    const audience = import.meta.env.AUTH0_AUDIENCE || apiBase;
    const encodedId = encodeURIComponent(userId);
    const resp = await fetch(
      `https://${auth0Domain}/api/v2/users/${encodedId}/permissions`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${mgmtToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          permissions: [
            {
              resource_server_identifier: audience,
              permission_name: permissionName,
            },
          ],
        }),
      },
    );

    if (!resp.ok) throw new Error(await resp.text());
  };

  /**
   * Remove multiple permissions from an Auth0 user
   * @param mgmtToken Token Auth0 Management API
   * @param userId Auth0 user ID (ex: auth0|xxx)
   * @param permissionNames Array of permission names
   */
  const removePermissionsFromUser = async (
    mgmtToken: string,
    userId: string,
    permissionNames: string[],
  ): Promise<void> => {
    if (permissionNames.length === 0) return;
    const apiBase = import.meta.env.API_BASE_URL || "";
    const audience = import.meta.env.AUTH0_AUDIENCE || apiBase;
    const encodedId = encodeURIComponent(userId);

    const permissionsPayload = permissionNames.map((name) => ({
      resource_server_identifier: audience,
      permission_name: name,
    }));

    const resp = await fetch(
      `https://${auth0Domain}/api/v2/users/${encodedId}/permissions`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${mgmtToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          permissions: permissionsPayload,
        }),
      },
    );

    if (!resp.ok) throw new Error(await resp.text());
  };

  /**
   * Update an Auth0 user's app_metadata
   * @param mgmtToken Token Auth0 Management API
   * @param userId Auth0 user ID
   * @param metadata The app_metadata object to merge/update
   */
  const updateUserAppMetadata = async (
    mgmtToken: string,
    userId: string,
    metadata: Record<string, unknown>,
  ): Promise<void> => {
    const encodedId = encodeURIComponent(userId);
    const resp = await fetch(
      `https://${auth0Domain}/api/v2/users/${encodedId}`,
      {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${mgmtToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          app_metadata: metadata,
        }),
      },
    );

    if (!resp.ok) throw new Error(await resp.text());
  };

  /**
   * Remove a permission from an Auth0 user
   * @param mgmtToken Token Auth0 Management API
   * @param userId Auth0 user ID (ex: auth0|xxx)
   * @param permissionName Permission name (ex: exercises:read)
   */
  const removePermissionFromUser = async (
    mgmtToken: string,
    userId: string,
    permissionName: string,
  ): Promise<void> => {
    const apiBase = import.meta.env.API_BASE_URL || "";
    const audience = import.meta.env.AUTH0_AUDIENCE || apiBase;
    const encodedId = encodeURIComponent(userId);
    const resp = await fetch(
      `https://${auth0Domain}/api/v2/users/${encodedId}/permissions`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${mgmtToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          permissions: [
            {
              resource_server_identifier: audience,
              permission_name: permissionName,
            },
          ],
        }),
      },
    );

    if (!resp.ok) throw new Error(await resp.text());
  };

  /**
   * Delete an Auth0 user
   * @param mgmtToken Token Auth0 Management API
   * @param userId Auth0 user ID (ex: auth0|xxx)
   */
  const deleteAuth0User = async (
    mgmtToken: string,
    userId: string,
  ): Promise<void> => {
    const encodedId = encodeURIComponent(userId);
    const resp = await fetch(
      `https://${auth0Domain}/api/v2/users/${encodedId}`,
      {
        method: "DELETE",
        headers: {
          Authorization: `Bearer ${mgmtToken}`,
          "Content-Type": "application/json",
        },
      },
    );

    if (!resp.ok) throw new Error(await resp.text());
  };

  /**
   * Fetches the list of blocked users (their Auth0 subs and block reasons) from our backend D1 database.
   * This is necessary because D1 `is_blocked=1` is the single source of truth for blocking.
   */
  const getD1BlockedUsers = async (
    skipCache: boolean = false,
  ): Promise<{ auth0_sub: string; block_reason: string | null }[]> => {
    const apiBase = import.meta.env.API_BASE_URL || "";

    try {
      const url = skipCache
        ? `${apiBase}/api/admin/users/blocked?t=${Date.now()}`
        : `${apiBase}/api/admin/users/blocked`;
      const data = await getJson<{
        success: boolean;
        blockedSubs: { auth0_sub: string; block_reason: string | null }[];
      }>(url);

      if (data && data.success && Array.isArray(data.blockedSubs)) {
        return data.blockedSubs;
      }

      return [];
    } catch {
      return [];
    }
  };

  /**
   * Get the list of Resource Servers (APIs) configured in Auth0
   * @param mgmtToken Token Auth0 Management API
   */
  const getResourceServers = async (
    mgmtToken: string,
  ): Promise<{ identifier: string; id: string }[]> => {
    const cacheKey = "resource_servers";
    const cached = getFromCache(cacheKey) as
      | { identifier: string; id: string }[]
      | null;

    if (cached) return cached;

    const resp = await fetch(`https://${auth0Domain}/api/v2/resource-servers`, {
      headers: {
        Authorization: `Bearer ${mgmtToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!resp.ok) throw new Error(await resp.text());

    const data = (await resp.json()) as { identifier: string; id: string }[];

    setToCache(cacheKey, data);

    return data;
  };

  /**
   * Update the scopes of an Auth0 Resource Server
   * @param mgmtToken Token Auth0 Management API
   * @param id Auth0 Resource Server ID (ex: "65f..." or its audience)
   * @param scopes List of scopes to define
   */
  const updateResourceServerScopes = async (
    mgmtToken: string,
    id: string,
    scopes: { value: string; description: string }[],
  ): Promise<void> => {
    // Note: Auth0 accepts the ID or the identifier (URL) for the PATCH /resource-servers/{id} endpoint
    const encodedId = encodeURIComponent(id);
    const resp = await fetch(
      `https://${auth0Domain}/api/v2/resource-servers/${encodedId}`,
      {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${mgmtToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          scopes,
        }),
      },
    );

    if (!resp.ok) throw new Error(await resp.text());
  };

  /**
   * Get the scopes (permissions) of a specific Auth0 Resource Server by its ID or identifier.
   * @param mgmtToken Auth0 Management API Token
   * @param id Auth0 Resource Server ID or identifier
   */
  const getResourceServerScopes = async (
    mgmtToken: string,
    id: string,
  ): Promise<{ value: string; description: string }[]> => {
    const cacheKey = `resource_server_scopes_${id}`;
    const cached = getFromCache(cacheKey) as
      | { value: string; description: string }[]
      | null;

    if (cached) return cached;

    const encodedId = encodeURIComponent(id);
    const resp = await fetch(
      `https://${auth0Domain}/api/v2/resource-servers/${encodedId}`,
      {
        headers: {
          Authorization: `Bearer ${mgmtToken}`,
          "Content-Type": "application/json",
        },
      },
    );

    if (!resp.ok) throw new Error(await resp.text());
    const data = (await resp.json()) as {
      scopes?: { value: string; description: string }[];
    };
    const scopes = data.scopes ?? [];

    setToCache(cacheKey, scopes);

    return scopes;
  };

  /**
   * Get the scopes (permissions) of a Resource Server using its audience.
   * @param mgmtToken Auth0 Management API Token
   * @param audience The audience identifier (URL) of the API
   */
  const getResourcesServerScopesWithAudience = async (
    mgmtToken: string,
    audience: string,
  ): Promise<{ value: string; description: string }[]> => {
    const servers = await getResourceServers(mgmtToken);
    const server = servers.find((s) => s.identifier === audience);

    if (!server)
      throw new Error(`Resource server with audience ${audience} not found`);

    return getResourceServerScopes(mgmtToken, server.id);
  };

  /**
   * Update the scopes of a Resource Server using its audience.
   * @param mgmtToken Auth0 Management API Token
   * @param audience The audience identifier of the API
   * @param scopes Complete list of scopes to set
   */
  const updateResourceServerScopesWithAudience = async (
    mgmtToken: string,
    audience: string,
    scopes: { value: string; description: string }[],
  ): Promise<void> => {
    const servers = await getResourceServers(mgmtToken);
    const server = servers.find((s) => s.identifier === audience);

    if (!server)
      throw new Error(`Resource server with audience ${audience} not found`);

    return updateResourceServerScopes(mgmtToken, server.id, scopes);
  };

  /**
   * Check if a set of scopes exactly matches those defined on a Resource Server.
   * @param mgmtToken Auth0 Management API Token
   * @param id Resource Server ID or identifier
   * @param targetScopes The scopes we want to verify
   * @returns true if synchronized, false if update is needed
   */
  const checkResourceServerScopes = async (
    mgmtToken: string,
    id: string,
    targetScopes: { value: string; description: string }[],
  ): Promise<boolean> => {
    const currentScopes = await getResourceServerScopes(mgmtToken, id);

    if (currentScopes.length !== targetScopes.length) return false;

    const currentValues = new Set(currentScopes.map((s) => s.value));
    const targetValues = new Set(targetScopes.map((s) => s.value));

    // Check if every target scope exists in current ones
    for (const val of targetValues) {
      if (!currentValues.has(val)) return false;
    }

    // Since lengths are equal, if all target are in current, they are identical
    return true;
  };

  /**
   * Check if scopes are synchronized using the audience to find the server.
   * @param mgmtToken Auth0 Management API Token
   * @param audience API audience
   * @param targetScopes Scopes to verify
   */
  const checkResourceServerScopesWithAudience = async (
    mgmtToken: string,
    audience: string,
    targetScopes: { value: string; description: string }[],
  ): Promise<boolean> => {
    const servers = await getResourceServers(mgmtToken);
    const server = servers.find((s) => s.identifier === audience);

    if (!server)
      throw new Error(`Resource server with audience ${audience} not found`);

    return checkResourceServerScopes(mgmtToken, server.id, targetScopes);
  };

  /**
   * Fetches metadata for all D1 users (blocked status, club names, etc.)
   */
  const getD1UserMetadata = async (
    skipCache: boolean = false,
  ): Promise<
    {
      id: string;
      auth0_sub: string;
      email: string;
      name: string;
      club_id: string | null;
      is_blocked: boolean;
      block_reason: string | null;
      siret: string | null;
      club_name: string | null;
      created_at: string;
      last_login: string | null;
      role: string;
    }[]
  > => {
    const apiBase = import.meta.env.API_BASE_URL || "";

    try {
      const url = skipCache
        ? `${apiBase}/api/admin/users/metadata?t=${Date.now()}`
        : `${apiBase}/api/admin/users/metadata`;
      const data = await getJson<{
        success: boolean;
        metadata: {
          id: string;
          auth0_sub: string;
          email: string;
          name: string;
          club_id: string | null;
          is_blocked: boolean;
          block_reason: string | null;
          siret: string | null;
          club_name: string | null;
          created_at: string;
          last_login: string | null;
          role: string;
        }[];
      }>(url);

      if (data && data.success && Array.isArray(data.metadata)) {
        return data.metadata;
      }

      return [];
    } catch {
      return [];
    }
  };

  return {
    getJson,
    postJson,
    deleteJson,
    hasPermission,
    putJson,
    getAccessToken,
    // Auth0 Management API
    getAuth0ManagementToken,
    listAuth0Users,
    getUserPermissions,
    addPermissionToUser,
    addPermissionsToUser,
    removePermissionFromUser,
    removePermissionsFromUser,
    deleteAuth0User,
    getResourceServers,
    updateResourceServerScopes,
    getResourceServerScopes,
    getResourcesServerScopesWithAudience,
    updateResourceServerScopesWithAudience,
    getD1BlockedUsers,
    getD1UserMetadata,
    checkResourceServerScopes,
    checkResourceServerScopesWithAudience,
    updateUserAppMetadata,
  };
};

/**
 * Composant de bannissement (Nuclear Guard)
 * Affiché immédiatement si l'utilisateur est bloqué.
 */
export const BlockedPage: FC<{ isBlocked: boolean; reason?: string }> = ({
  isBlocked,
  reason,
}) => {
  const { logout } = useAuth();

  if (!isBlocked) return null;

  return (
    <div className="fixed inset-0 z-9999 flex items-center justify-center bg-black p-4 sm:p-6">
      <div className="max-w-lg w-full bg-zinc-900 border-2 border-red-600 shadow-2xl shadow-red-900/40 rounded-3xl p-6 sm:p-10 text-center flex flex-col items-center gap-5 animate-appearance-in">
        {/* Icon */}
        <div className="p-4 rounded-full bg-red-600/20 border-2 border-red-600/40">
          <svg
            className="w-14 h-14 text-red-500"
            fill="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              clipRule="evenodd"
              d="M12 2.25c-5.385 0-9.75 4.365-9.75 9.75s4.365 9.75 9.75 9.75 9.75-4.365 9.75-9.75S17.385 2.25 12 2.25zm-1.72 6.97a.75.75 0 10-1.06 1.06L10.94 12l-1.72 1.72a.75.75 0 101.06 1.06L12 13.06l1.72 1.72a.75.75 0 101.06-1.06L13.06 12l1.72-1.72a.75.75 0 10-1.06-1.06L12 10.94l-1.72-1.72z"
              fillRule="evenodd"
            />
          </svg>
        </div>

        {/* Title */}
        <h1 className="text-xl sm:text-2xl font-black text-red-500 tracking-tight leading-tight">
          Accès restreint
        </h1>

        {/* Message de Support Unique */}
        <div className="w-full bg-red-950/20 border border-red-800/20 rounded-2xl p-6">
          <p className="text-base sm:text-lg font-medium text-red-200 leading-relaxed mb-4">
            {reason ? (
              <>
                Votre compte a été suspendu pour le motif suivant : <br />
                <span className="text-white font-bold">"{reason}"</span>
              </>
            ) : (
              "Votre compte a été temporairement suspendu."
            )}
          </p>
          <div className="h-px bg-red-800/20 w-full mb-4" />
          <p className="text-sm font-medium text-red-300/80 italic">
            Pour plus d'informations, veuillez contacter le support.
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-3 w-full mt-2">
          <a
            className="flex items-center justify-center gap-2 bg-white text-black font-black tracking-tight rounded-2xl h-12 text-sm shadow-lg hover:shadow-white/20 hover:scale-[1.02] transition-all w-full"
            href="mailto:support@kdufoot.com?subject=Demande%20de%20débannissement%20KduFoot"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M21.75 6.75v10.5a2.25 2.25 0 0 1-2.25 2.25h-15a2.25 2.25 0 0 1-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25m19.5 0v.243a2.25 2.25 0 0 1-1.07 1.916l-7.5 4.615a2.25 2.25 0 0 1-2.36 0L3.32 8.91a2.25 2.25 0 0 1-1.07-1.916V6.75"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            CONTACTER LE SUPPORT
          </a>
          <button
            className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white font-bold tracking-tight rounded-2xl h-12 text-sm shadow-lg transition-all w-full cursor-pointer"
            onClick={() =>
              logout({ logoutParams: { returnTo: window.location.origin } })
            }
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M15.75 9V5.25A2.25 2.25 0 0 0 13.5 3h-6a2.25 2.25 0 0 0-2.25 2.25v13.5A2.25 2.25 0 0 0 7.5 21h6a2.25 2.25 0 0 0 2.25-2.25V15m3 0 3-3m0 0-3-3m3 3H9"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            DÉCONNEXION
          </button>
        </div>

        <p className="text-xs sm:text-sm text-white/20 mt-2">
          support@kdufoot.com
        </p>
      </div>
    </div>
  );
};

/**
 * AutoPermissionProvisioner Component
 *
 * This component manages the automatic provisioning of permissions defined in
 * `import.meta.env.AUTH0_AUTOMATIC_PERMISSIONS`.
 */
export const AutoPermissionProvisioner: FC<{ children: ReactNode }> = ({
  children,
}) => {
  const {
    isAuthenticated,
    isLoading,
    user,
    hasPermission,
    postJson,
    getAccessToken,
  } = useAuth();
  const [isProvisioning, setIsProvisioning] = useState(false);

  useEffect(() => {
    // 1. Loading/Auth Guard: Don't act while Auth0 is still initializing
    if (isLoading || !isAuthenticated || isProvisioning) return;

    const userId = user?.sub;

    if (!userId) return;

    // 2. Configuration: Retrieve the list of target permissions from environment
    const autoPerms = import.meta.env.AUTH0_AUTOMATIC_PERMISSIONS;

    if (!autoPerms || !Array.isArray(autoPerms) || autoPerms.length === 0)
      return;

    const checkAndProvision = async () => {
      // 3. Permission Detection: Check which target permissions are currently missing
      const missingPerms: string[] = [];

      for (const p of autoPerms) {
        const has = await hasPermission(p);

        if (!has) missingPerms.push(p);
      }

      const storageKey = `autoperm_attempted_${userId}`;

      if (missingPerms.length === 0) {
        // Clear flag if we have all permissions (either initially or after provisioning)
        if (sessionStorage.getItem(storageKey)) {
          sessionStorage.removeItem(storageKey);
        }

        return;
      }

      // If missing permissions, check if we already tried in this session
      if (sessionStorage.getItem(storageKey) === "true") return;

      setIsProvisioning(true);
      sessionStorage.setItem(storageKey, "true");
      try {
        const apiBase = import.meta.env.API_BASE_URL || "";
        const result = await postJson<{ success: boolean }>(
          `${apiBase}/api/__auth0/autopermissions`,
          {},
        );

        if (result.success) {
          // 6. Token Refresh
          await getAccessToken({ cacheMode: "off" });

          // Refresh all data silently
          mutate(() => true, undefined, { revalidate: true });
        }
      } catch (error) {
        console.error("Auto-provisioning failed:", error);
      } finally {
        setIsProvisioning(false);
      }
    };

    checkAndProvision();
  }, [
    isAuthenticated,
    isLoading,
    user,
    hasPermission,
    postJson,
    getAccessToken,
    isProvisioning,
  ]);

  if (isProvisioning) {
    return <SiteLoading />;
  }

  return <>{children}</>;
};
